// Loads the AudioWorklet processor modules and builds the per-slot Voice config.
//
// Uses `new URL(..., import.meta.url)` so Vite transpiles/emits each processor
// as a same-origin asset (worklets are authored in JS so the asset pipeline,
// which copies verbatim, produces a valid module — a .ts would not be).
//
// Slot mapping: 0 = analog/PWM, 1 = wavetable, 2 = FM 4-op (FM lands next).

import { generateWavetableBank } from './wavetables';

interface WorkletModule {
  name: string;
  url: URL;
}

export interface OscSlotConfig {
  name: string;
  processorOptions?: Record<string, unknown>;
}

const MODULES: WorkletModule[] = [
  { name: 'analog-pwm', url: new URL('./analog-pwm.worklet.js', import.meta.url) },
  { name: 'wavetable', url: new URL('./wavetable.worklet.js', import.meta.url) },
  { name: 'fm4op', url: new URL('./fm4op.worklet.js', import.meta.url) },
];

/** True when the browser supports AudioWorklet (per D2 fallback). */
export function supportsAudioWorklet(ctx: BaseAudioContext): boolean {
  return typeof (ctx as AudioContext).audioWorklet?.addModule === 'function';
}

export async function loadOscillatorWorklets(ctx: AudioContext): Promise<void> {
  for (const m of MODULES) {
    await ctx.audioWorklet.addModule(m.url);
  }
}

/** Build the three Voice oscillator slots (`null` = slot left silent). */
export function buildSlotConfigs(): readonly (OscSlotConfig | null)[] {
  const bank = generateWavetableBank();
  return [
    { name: 'analog-pwm' },
    {
      name: 'wavetable',
      processorOptions: {
        tableSize: bank.tableSize,
        count: bank.count,
        samples: bank.samples,
      },
    },
    { name: 'fm4op' },
  ];
}
